/** EE / EO topics: original tasks on the themes seen at the exam, validated by a trainer. */

import {
  type AcademyTasksListParams,
  type ContentStatusEnum,
  type Task,
  type TaskSkillEnum,
  useAcademyTasksCreate,
  useAcademyTasksList,
  useAcademyTasksPartialUpdate,
  useAcademyTasksPublishCreate,
  useAcademyTasksReviewCreate,
} from "@agencyos/api-client";
import { contentLanguage } from "@agencyos/core";
import { Badge, Button, GlassCard, GlassSheet, SelectField, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { CheckCheck, Plus, Rocket, Trash2 } from "lucide-react";
import { type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { useAdminExam, useExamTexts } from "./examContext";
import { isTooClose, OriginalContentBanner, TooSimilarAlert } from "./QuestionsTab";
import { CONTENT_STATUSES, ErrorList, isObject, nextKey, Pager, percent, StatusBadge, str, useAcademyRoles, useRefreshAcademy } from "./shared";

const SKILLS: TaskSkillEnum[] = ["ee", "eo"];
const NUMBERS = [1, 2, 3] as const;
const NCLC_LEVELS = [4, 5, 6, 7, 8, 9, 10] as const;

/** The API filters on these query parameters (not declared in the OpenAPI schema yet). */
type TaskFilters = AcademyTasksListParams;

interface DocRow {
  title: string;
  text: string;
}
interface FactRow {
  key: number;
  question: string;
  answer: string;
}
interface TextRow {
  key: number;
  text: string;
}
interface ModelRow {
  key: number;
  nclc: string;
  text: string;
}

interface TaskForm {
  skill: TaskSkillEnum;
  number: number;
  theme: string;
  prompt: string;
  session: string;
  center: string;
  documents: [DocRow, DocRow];
  role: string;
  facts: FactRow[];
  followUps: TextRow[];
  models: ModelRow[];
}

/** « [À rédiger par un formateur] … »: a draft created from a learner's exam feedback. */
export function isFromFeedback(task: Pick<Task, "prompt">): boolean {
  return /^\s*\[[^\]]+\]/.test(task.prompt);
}

function docsOf(value: unknown): [DocRow, DocRow] {
  const list = Array.isArray(value) ? (value as unknown[]) : [];
  const doc = (d: unknown): DocRow => (isObject(d) ? { title: str(d.title), text: str(d.text) || str(d.body) } : { title: "", text: str(d) });
  return [doc(list[0]), doc(list[1])];
}

function toForm(task: Task | undefined): TaskForm {
  const script = isObject(task?.examiner_script) ? task.examiner_script : {};
  const facts = isObject(script.facts)
    ? Object.entries(script.facts).map(([question, answer]) => ({ key: nextKey(), question, answer: str(answer) }))
    : Array.isArray(script.facts)
      ? (script.facts as unknown[]).filter(isObject).map((f) => ({ key: nextKey(), question: str(f.key ?? f.question), answer: str(f.answer) }))
      : [];
  const followUps = Array.isArray(script.follow_ups) ? (script.follow_ups as unknown[]).map((text) => ({ key: nextKey(), text: str(text) })) : [];
  const models = isObject(task?.model_answers)
    ? Object.entries(task.model_answers).map(([nclc, text]) => ({ key: nextKey(), nclc, text: str(text) }))
    : [];
  return {
    skill: task?.skill ?? "ee",
    number: task?.number ?? 1,
    theme: task?.theme ?? "",
    prompt: task?.prompt ?? "",
    session: task?.session ?? "",
    center: task?.center ?? "",
    documents: docsOf(task?.documents),
    role: str(script.role),
    facts,
    followUps,
    models,
  };
}

/** Examiner script of EO task 2, kept JSON-friendly: { role, facts: {question: answer}, follow_ups: [] }. */
export function scriptOf(form: Pick<TaskForm, "role" | "facts" | "followUps">, previous: unknown): Record<string, unknown> {
  const base = isObject(previous) ? previous : {};
  return {
    ...base,
    role: form.role.trim(),
    facts: Object.fromEntries(form.facts.filter((f) => f.question.trim()).map((f) => [f.question.trim(), f.answer.trim()])),
    follow_ups: form.followUps.map((f) => f.text.trim()).filter(Boolean),
  };
}

function TaskSheet({ task, onClose }: { task: Task | undefined; onClose: () => void }) {
  const { t } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const create = useAcademyTasksCreate({ mutation: { onSuccess: refresh } });
  const update = useAcademyTasksPartialUpdate({ mutation: { onSuccess: refresh } });
  const review = useAcademyTasksReviewCreate({ mutation: { onSuccess: refresh } });
  const publish = useAcademyTasksPublishCreate({ mutation: { onSuccess: refresh } });
  const [current, setCurrent] = useState<Task | undefined>(task);
  const [form, setForm] = useState<TaskForm>(() => toForm(task));
  const set = <K extends keyof TaskForm>(key: K, value: TaskForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const saveError = current ? update.error : create.error;
  const errors = fieldErrors(saveError);
  const pending = create.isPending || update.isPending || review.isPending || publish.isPending;
  const status: ContentStatusEnum = current?.status ?? "draft";
  const withDocuments = form.skill === "ee" && form.number === 3;
  const withScript = form.skill === "eo" && form.number === 2;

  const payload = () => ({
    skill: form.skill,
    number: form.number,
    theme: form.theme.trim(),
    prompt: form.prompt,
    session: form.session.trim(),
    center: form.center.trim(),
    documents: withDocuments ? form.documents.filter((d) => d.text.trim()).map((d) => ({ title: d.title.trim(), text: d.text })) : [],
    examiner_script: withScript ? scriptOf(form, current?.examiner_script) : {},
    model_answers: Object.fromEntries(form.models.filter((m) => m.text.trim()).map((m) => [m.nclc, m.text])),
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    review.reset();
    publish.reset();
    if (current) update.mutate({ id: current.id, data: payload() }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });
    else create.mutate({ data: payload() }, { onSuccess: (r) => r.status === 201 && setCurrent(r.data) });
  };
  const onReview = () => {
    if (!current) return;
    publish.reset();
    review.mutate({ id: current.id }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });
  };
  const onPublish = () => {
    if (!current) return;
    review.reset();
    publish.mutate({ id: current.id }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });
  };
  const setDoc = (index: 0 | 1, field: keyof DocRow, value: string) =>
    set("documents", form.documents.map((d, i) => (i === index ? { ...d, [field]: value } : d)) as TaskForm["documents"]);
  const usedNclc = new Set(form.models.map((m) => m.nclc));
  const freeNclc = NCLC_LEVELS.map(String).find((n) => !usedNclc.has(n));

  return (
    <GlassSheet open title={current ? t("academyAdmin.topics.edit") : t("academyAdmin.topics.new")} onClose={onClose}>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <OriginalContentBanner />
        {current ? (
          <div className="row">
            <StatusBadge status={status} />
            {isFromFeedback(current) ? <Badge tone="offer">{t("academyAdmin.topics.fromFeedback")}</Badge> : null}
            <span className="muted">{t("academyAdmin.similarity", { value: percent(current.similarity) })}</span>
          </div>
        ) : null}
        {current && isFromFeedback(current) ? <p className="muted">{t("academyAdmin.topics.fromFeedbackHint")}</p> : null}
        <fieldset className="plain-fieldset stack" disabled={!canEdit}>
          <div className="form-grid">
            <SelectField
              label={t("academyAdmin.skillLabel")}
              value={form.skill}
              options={SKILLS.map((s) => ({ value: s, label: t(`academyAdmin.skill.${s}`) }))}
              onChange={(e) => set("skill", e.target.value as TaskSkillEnum)}
            />
            <SelectField
              label={t("academyAdmin.topics.number")}
              value={String(form.number)}
              error={errors.number}
              options={NUMBERS.map((n) => ({ value: String(n), label: t("academyAdmin.topics.task", { n }) }))}
              onChange={(e) => set("number", Number(e.target.value))}
            />
            <TextField label={t("academyAdmin.theme")} value={form.theme} maxLength={80} error={errors.theme} onChange={(e) => set("theme", e.target.value)} />
            <TextField
              label={t("academyAdmin.topics.session")}
              hint={t("academyAdmin.topics.sessionHint")}
              value={form.session}
              maxLength={20}
              error={errors.session}
              onChange={(e) => set("session", e.target.value)}
            />
            <TextField label={t("academyAdmin.topics.center")} value={form.center} maxLength={60} error={errors.center} onChange={(e) => set("center", e.target.value)} />
          </div>
          <TextAreaField
            label={t("academyAdmin.topics.prompt")}
            lang="fr"
            rows={5}
            value={form.prompt}
            error={errors.prompt}
            onChange={(e) => set("prompt", e.target.value)}
          />

          {withDocuments ? (
            <div className="stack">
              <h3>{t("academyAdmin.topics.documents")}</h3>
              <p className="muted">{t("academyAdmin.topics.documentsHint")}</p>
              {([0, 1] as const).map((i) => (
                <div key={i} className="installment-row stack">
                  <TextField
                    label={t("academyAdmin.topics.documentTitle", { n: i + 1 })}
                    lang="fr"
                    value={form.documents[i].title}
                    onChange={(e) => setDoc(i, "title", e.target.value)}
                  />
                  <TextAreaField
                    label={t("academyAdmin.topics.documentText", { n: i + 1 })}
                    lang="fr"
                    rows={5}
                    value={form.documents[i].text}
                    onChange={(e) => setDoc(i, "text", e.target.value)}
                  />
                </div>
              ))}
              {errors.documents ? (
                <p role="alert" className="ao-field__error">
                  {errors.documents}
                </p>
              ) : null}
            </div>
          ) : null}

          {withScript ? (
            <div className="stack">
              <h3>{t("academyAdmin.topics.script")}</h3>
              <p className="muted">{t("academyAdmin.topics.scriptHint")}</p>
              <TextField
                label={t("academyAdmin.topics.role")}
                hint={t("academyAdmin.topics.roleHint")}
                lang="fr"
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
              />
              <strong>{t("academyAdmin.topics.facts")}</strong>
              {form.facts.length ? null : <p className="muted">{t("academyAdmin.topics.noFacts")}</p>}
              <ol className="plain-list stack">
                {form.facts.map((f) => (
                  <li key={f.key} className="installment-row">
                    <div className="form-grid">
                      <TextField
                        label={t("academyAdmin.topics.factQuestion")}
                        lang="fr"
                        value={f.question}
                        onChange={(e) => set("facts", form.facts.map((x) => (x.key === f.key ? { ...x, question: e.target.value } : x)))}
                      />
                      <TextField
                        label={t("academyAdmin.topics.factAnswer")}
                        lang="fr"
                        value={f.answer}
                        onChange={(e) => set("facts", form.facts.map((x) => (x.key === f.key ? { ...x, answer: e.target.value } : x)))}
                      />
                    </div>
                    <div className="row">
                      <Button
                        variant="ghost"
                        aria-label={t("common.remove")}
                        title={t("common.remove")}
                        onClick={() => set("facts", form.facts.filter((x) => x.key !== f.key))}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="row">
                <Button
                  variant="secondary"
                  icon={<Plus size={16} aria-hidden="true" />}
                  onClick={() => set("facts", [...form.facts, { key: nextKey(), question: "", answer: "" }])}
                >
                  {t("academyAdmin.topics.addFact")}
                </Button>
              </div>
              <strong>{t("academyAdmin.topics.followUps")}</strong>
              <ol className="plain-list stack">
                {form.followUps.map((f, n) => (
                  <li key={f.key} className="row academy-row">
                    <TextField
                      className="academy-grow"
                      label={t("academyAdmin.topics.followUp", { n: n + 1 })}
                      lang="fr"
                      value={f.text}
                      onChange={(e) => set("followUps", form.followUps.map((x) => (x.key === f.key ? { ...x, text: e.target.value } : x)))}
                    />
                    <Button
                      variant="ghost"
                      aria-label={t("common.remove")}
                      title={t("common.remove")}
                      onClick={() => set("followUps", form.followUps.filter((x) => x.key !== f.key))}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ol>
              <div className="row">
                <Button
                  variant="secondary"
                  icon={<Plus size={16} aria-hidden="true" />}
                  onClick={() => set("followUps", [...form.followUps, { key: nextKey(), text: "" }])}
                >
                  {t("academyAdmin.topics.addFollowUp")}
                </Button>
              </div>
              {errors.examiner_script ? (
                <p role="alert" className="ao-field__error">
                  {errors.examiner_script}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="stack">
            <h3>{t("academyAdmin.topics.models")}</h3>
            <p className="muted">{t("academyAdmin.topics.modelsHint")}</p>
            <ol className="plain-list stack">
              {form.models.map((m) => (
                <li key={m.key} className="installment-row stack">
                  <div className="row between">
                    <SelectField
                      label={t("academyAdmin.topics.nclc")}
                      value={m.nclc}
                      options={NCLC_LEVELS.map((n) => ({ value: String(n), label: `NCLC ${n}` })).filter(
                        (o) => o.value === m.nclc || !usedNclc.has(o.value),
                      )}
                      onChange={(e) => set("models", form.models.map((x) => (x.key === m.key ? { ...x, nclc: e.target.value } : x)))}
                    />
                    <Button
                      variant="ghost"
                      aria-label={t("common.remove")}
                      title={t("common.remove")}
                      onClick={() => set("models", form.models.filter((x) => x.key !== m.key))}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </Button>
                  </div>
                  <TextAreaField
                    label={t("academyAdmin.topics.modelText", { nclc: m.nclc })}
                    lang="fr"
                    rows={6}
                    value={m.text}
                    onChange={(e) => set("models", form.models.map((x) => (x.key === m.key ? { ...x, text: e.target.value } : x)))}
                  />
                </li>
              ))}
            </ol>
            <div className="row">
              <Button
                variant="secondary"
                icon={<Plus size={16} aria-hidden="true" />}
                disabled={!freeNclc}
                onClick={() => freeNclc && set("models", [...form.models, { key: nextKey(), nclc: freeNclc, text: "" }])}
              >
                {t("academyAdmin.topics.addModel")}
              </Button>
            </div>
            {errors.model_answers ? (
              <p role="alert" className="ao-field__error">
                {errors.model_answers}
              </p>
            ) : null}
          </div>

          {saveError && !Object.keys(errors).some((k) => k !== "detail" && k !== "non_field_errors") ? <ErrorList error={saveError} /> : null}
          <div className="row">
            <Button type="submit" disabled={pending}>
              {pending ? t("common.loading") : current ? t("common.save") : t("academyAdmin.create")}
            </Button>
            {update.isSuccess || create.isSuccess ? (
              <span role="status" className="ok">
                {current && status === "draft" ? t("academyAdmin.savedAsDraft") : t("common.saved")}
              </span>
            ) : null}
          </div>
          {current ? <p className="muted">{t("academyAdmin.editBackToDraft")}</p> : null}
        </fieldset>
      </form>

      {current && canEdit ? (
        <section className="stack">
          <h3>{t("academyAdmin.validation")}</h3>
          <p className="muted">{t("academyAdmin.validationHint")}</p>
          <div className="row">
            <Button variant="secondary" icon={<CheckCheck size={16} aria-hidden="true" />} disabled={pending || status !== "draft"} onClick={onReview}>
              {t("academyAdmin.review")}
            </Button>
            <Button icon={<Rocket size={16} aria-hidden="true" />} disabled={pending || status === "published"} onClick={onPublish}>
              {t("academyAdmin.publish")}
            </Button>
          </div>
          <TooSimilarAlert error={publish.error ?? review.error} />
          <ErrorList error={review.error ?? (isTooClose(publish.error) ? null : publish.error)} />
          {review.isSuccess || publish.isSuccess ? (
            <span role="status" className="ok">
              {t(`academyAdmin.status.${status}`)}
            </span>
          ) : null}
        </section>
      ) : null}
    </GlassSheet>
  );
}

export function TopicsTab() {
  const { t } = useTranslation();
  const { canEdit: canEditRole } = useAcademyRoles();
  const { exam, ielts, passages, contentParams: params } = useAdminExam();
  // IELTS topics (Writing letters and essays, Speaking parts) come from the IELTS loader; this form
  // writes the TCF tasks only, so the IELTS list is read here and never overwritten.
  // D-123: the TEF and German items come from their loaders too (this form has no exam: it
  // created a TCF item while the TEF or German list was shown)
  const canEdit = canEditRole && !passages;
  const texts = useExamTexts();
  const skillLabel = texts.skill; // D-123: Hören… for German
  const [skill, setSkill] = useState("");
  const [number, setNumber] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Task | "new" | null>(null);
  const close = useCallback(() => setEditing(null), []);
  const filters: TaskFilters = { page, ...params, skill: skill || undefined, number: number ? Number(number) : undefined, status: status || undefined };
  const list = useAcademyTasksList(filters);
  const data = list.data?.status === 200 ? list.data.data : undefined;
  const filter = (apply: (value: string) => void) => (value: string) => {
    apply(value);
    setPage(1);
  };

  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{ielts ? t("academyAdmin.ielts.topics.title") : texts.title("topics", "academyAdmin.topics.title")}</h2>
          <p className="muted">{ielts ? t("academyAdmin.ielts.topics.intro") : t("academyAdmin.topics.intro")}</p>
        </div>
        {canEdit ? (
          <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setEditing("new")}>
            {t("academyAdmin.topics.new")}
          </Button>
        ) : null}
      </div>
      <OriginalContentBanner />
      <div className="form-grid">
        <SelectField
          label={t("academyAdmin.skillLabel")}
          value={skill}
          options={[{ value: "", label: t("academyAdmin.all") }, ...SKILLS.map((s) => ({ value: s, label: skillLabel(s) }))]}
          onChange={(e) => filter(setSkill)(e.target.value)}
        />
        <SelectField
          label={t("academyAdmin.topics.number")}
          value={number}
          options={[{ value: "", label: t("academyAdmin.all") }, ...NUMBERS.map((n) => ({ value: String(n), label: t("academyAdmin.topics.task", { n }) }))]}
          onChange={(e) => filter(setNumber)(e.target.value)}
        />
        <SelectField
          label={t("academyAdmin.statusLabel")}
          value={status}
          options={[{ value: "", label: t("academyAdmin.all") }, ...CONTENT_STATUSES.map((s) => ({ value: s, label: t(`academyAdmin.status.${s}`) }))]}
          onChange={(e) => filter(setStatus)(e.target.value)}
        />
      </div>
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : data.results.length ? (
        <>
          <p className="muted">{t("academyAdmin.count", { count: data.count })}</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.topics.prompt")}</th>
                  <th>{t("academyAdmin.topics.number")}</th>
                  <th>{t("academyAdmin.theme")}</th>
                  <th>{t("academyAdmin.topics.seen")}</th>
                  <th>{t("academyAdmin.similarityShort")}</th>
                  <th>{t("academyAdmin.statusLabel")}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((task) => (
                  <tr key={task.id}>
                    <td>
                      {passages ? (
                        <span className="academy-clamp" lang={contentLanguage(exam)}>
                          {task.prompt}
                        </span>
                      ) : (
                        <button type="button" className="link academy-clamp" lang="fr" onClick={() => setEditing(task)}>
                          {task.prompt}
                        </button>
                      )}
                      {isFromFeedback(task) ? (
                        <>
                          {" "}
                          <Badge tone="offer">{t("academyAdmin.topics.fromFeedback")}</Badge>
                        </>
                      ) : null}
                    </td>
                    <td className="nowrap">
                      {task.skill.toUpperCase()} · {t("academyAdmin.topics.task", { n: task.number })}
                    </td>
                    <td>{task.theme || "—"}</td>
                    <td>{[task.session, task.center].filter(Boolean).join(" · ") || "—"}</td>
                    <td>{percent(task.similarity)}</td>
                    <td>
                      <StatusBadge status={task.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : (
        <p className="muted">{t("academyAdmin.topics.empty")}</p>
      )}
      {editing ? <TaskSheet key={editing === "new" ? "new" : editing.id} task={editing === "new" ? undefined : editing} onClose={close} /> : null}
    </GlassCard>
  );
}
